import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { setCredentials } from "../../redux/slices/authSlice";
import { useMarkAnnouncementSeenMutation } from "../../redux/slices/api/authApiSlice";
import { ANNOUNCEMENT_VERSION } from "../../data/announcements";

const SEEN_KEY = "seenAnnouncement";

// Controls the "What's New" popup. Auto-opens once per release for users who
// haven't seen the current ANNOUNCEMENT_VERSION; logged-in users are tracked on
// their account, guests via localStorage. Also exposes openWhatsNew() so the
// Header can reopen it on demand.
const WhatsNewContext = createContext(null);

export function WhatsNewProvider({ children }) {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [markSeen] = useMarkAnnouncementSeenMutation();

  // Guard so the auto-open evaluation only fires once per mount.
  const evaluatedRef = useRef(false);

  const openWhatsNew = useCallback(() => setOpen(true), []);

  // Record "seen" for the current version and close.
  const dismiss = useCallback(() => {
    setOpen(false);

    if (user) {
      markSeen(ANNOUNCEMENT_VERSION);
      // Keep local user state + persisted copy in sync without a refetch.
      dispatch(
        setCredentials({
          user: { ...user, lastSeenAnnouncement: ANNOUNCEMENT_VERSION },
        }),
      );
    } else {
      localStorage.setItem(SEEN_KEY, ANNOUNCEMENT_VERSION);
    }
  }, [user, markSeen, dispatch]);

  // Auto-open on first load if this version hasn't been seen.
  useEffect(() => {
    if (evaluatedRef.current) return;
    evaluatedRef.current = true;

    const seen = user
      ? user.lastSeenAnnouncement === ANNOUNCEMENT_VERSION
      : localStorage.getItem(SEEN_KEY) === ANNOUNCEMENT_VERSION;

    if (!seen) setOpen(true);
  }, [user]);

  return (
    <WhatsNewContext.Provider value={{ open, openWhatsNew, dismiss }}>
      {children}
    </WhatsNewContext.Provider>
  );
}

export const useWhatsNew = () => useContext(WhatsNewContext);
